import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAuthUser, getUserTypeName } from "../../admin/navigation/adminNavigation";

/**
 * Route protection guard for the Partner Onboarding flow and Partner areas.
 * Checks authentication, profile completion, subscription, documents, and verification state.
 */
export const PartnerRouteGuard = ({
  children,
  requireAuth = true,
  requireProfile = false,
  requireSubscription = false,
  requireDocuments = false,
  requireVerified = false,
}) => {
  const authState = useSelector((state) => state.auth);
  const partnerOnboarding = useSelector((state) => state.partnerOnboarding);
  const location = useLocation();

  const user = getAuthUser(authState);
  const userType = getUserTypeName(authState);
  const isAuthenticated = Boolean(authState.isAuthenticated && user);

  // If page requires auth and user is not authenticated -> redirect to signup / login
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to="/partner/signup"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  // If authenticated but user is not a PARTNER (e.g. CUSTOMER)
  if (isAuthenticated && userType && userType !== "PARTNER" && userType !== "SUPER_ADMIN" && userType !== "ALL_HOME_DESIRE") {
    return <Navigate to="/customer/myspace" replace />;
  }

  // Check profile completeness if required
  if (requireProfile) {
    const hasFirstName = Boolean(user?.first_name || partnerOnboarding.profile.first_name);
    const hasServiceId = Boolean(user?.service_id || partnerOnboarding.profile.service_id);
    if (!hasFirstName || !hasServiceId) {
      return <Navigate to="/partner/onboarding" replace />;
    }
  }

  // Check subscription if required
  if (requireSubscription) {
    const isSubActive = partnerOnboarding.subscriptionStatus === "ACTIVE";
    if (!isSubActive) {
      return <Navigate to="/partner/subscription" replace />;
    }
  }

  // Check document upload if required
  if (requireDocuments) {
    const serviceId = String(user?.service_id || partnerOnboarding.profile.service_id || "1");
    const docs = partnerOnboarding.uploadedDocumentTypes || {};
    const hasAadhaar = Boolean(docs.aadhar || docs.aadhaar);
    const hasPan = Boolean(docs.pan);
    const hasRera = serviceId === "2" ? Boolean(docs.reraregistration || docs.rera) : true;

    if (!hasAadhaar || !hasPan || !hasRera) {
      return <Navigate to="/partner/documents" replace />;
    }
  }

  // Check verification approval if required (e.g. for /partner/dashboard)
  if (requireVerified) {
    const verificationStatus = partnerOnboarding.verificationStatus;
    if (verificationStatus === "DOCUMENTS_PENDING" || verificationStatus === "NEW") {
      return <Navigate to="/partner/documents" replace />;
    }
    if (verificationStatus === "UNDER_REVIEW") {
      return <Navigate to="/partner/verification" replace />;
    }
  }

  return children;
};

export default PartnerRouteGuard;
